import { useEffect, useState, FC, StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { store } from './store';
import App from './App';
import { ColorModeProvider } from './theme/ColorMode';
import TypesafeI18n from './i18n/i18n-react';
import { detectLocale, isLocale } from './i18n/i18n-util';
import { loadAllLocales } from './i18n/i18n-util.sync';
import type { Locales } from './i18n/i18n-types';
import { CircularProgress, Stack } from '@mui/material';

// Load all translation files
loadAllLocales();

const AppWrapper: FC = () => {
    const [locale, setLocale] = useState<Locales>('en');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const stored = (localStorage.getItem('lang') || '').trim();
        // A stale or hand-edited value must not put i18n into an unknown locale.
        if (isLocale(stored)) {
            setLocale(stored);
            setIsLoading(false);
            return;
        }
        fetch('/rest/Settings', { credentials: 'include' })
            .then((res) => res.json())
            .then((data) => {
                const defaultLang = String(data?.defaultLanguage ?? '');
                setLocale(isLocale(defaultLang) ? defaultLang : detectLocale());
            })
            .catch(() => {
                setLocale(detectLocale());
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    // Keep <html lang> in sync so screen readers and the browser use the right language.
    useEffect(() => {
        document.documentElement.lang = locale;
    }, [locale]);

    if (isLoading) {
        return (
            <Stack sx={{ alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Stack>
        );
    }

    return (
        <Provider store={store}>
            <TypesafeI18n locale={locale}>
                <BrowserRouter>
                    <App />
                </BrowserRouter>
            </TypesafeI18n>
        </Provider>
    );
};

ReactDOM.createRoot(document.getElementById('root')!).render(
    <StrictMode>
        {/* Outside AppWrapper so even the initial spinner is themed. */}
        <ColorModeProvider>
            <AppWrapper />
        </ColorModeProvider>
    </StrictMode>
);
